import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  contentRef: React.RefObject<HTMLDivElement>;
  instanceId: string;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | null>(null);

// Global registry to track open dropdowns
const openDropdowns = new Set<string>();

// Function to close all other dropdowns except the one with the given ID
const closeOtherDropdowns = (currentId: string) => {
  openDropdowns.forEach(id => {
    if (id !== currentId) {
      // Dispatch a custom event to close this dropdown
      window.dispatchEvent(new CustomEvent('close-dropdown', { detail: { id } }));
    }
  });
};

interface DropdownMenuProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const DropdownMenu = ({ children, open: controlledOpen, onOpenChange }: DropdownMenuProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const instanceId = React.useMemo(() => `dropdown-${Math.random().toString(36).substring(2, 11)}`, []);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = React.useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        // Close all other dropdowns when opening this one
        closeOtherDropdowns(instanceId);
        openDropdowns.add(instanceId);
      } else {
        openDropdowns.delete(instanceId);
      }

      if (controlledOpen === undefined) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [controlledOpen, onOpenChange, instanceId]
  );

  // Listen for close events from other dropdowns
  React.useEffect(() => {
    const handleCloseEvent = (event: CustomEvent) => {
      if (event.detail.id === instanceId) {
        setOpen(false);
      }
    };

    window.addEventListener('close-dropdown', handleCloseEvent as EventListener);
    return () => {
      window.removeEventListener('close-dropdown', handleCloseEvent as EventListener);
      // Clean up on unmount
      openDropdowns.delete(instanceId);
    };
  }, [instanceId, setOpen]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside trigger or content - if so, don't close
      const clickedInsideTrigger = triggerRef.current && triggerRef.current.contains(target);
      const clickedInsideContent = contentRef.current && contentRef.current.contains(target);

      // Only close if click is outside both trigger and content
      if (!clickedInsideTrigger && !clickedInsideContent) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    // Use click event in bubble phase so React's synthetic events execute first
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, setOpen, contentRef, triggerRef]);

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, triggerRef, contentRef, instanceId }}>
      {children}
    </DropdownMenuContext.Provider>
  );
};

interface DropdownMenuTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  children: React.ReactNode;
}

const DropdownMenuTrigger = React.forwardRef<HTMLButtonElement, DropdownMenuTriggerProps>(
  ({ asChild, children, onClick, ...props }, ref) => {
    const context = React.useContext(DropdownMenuContext);
    if (!context) throw new Error('DropdownMenuTrigger must be used within DropdownMenu');

    const { open, setOpen, triggerRef } = context;
    const internalRef = React.useRef<HTMLButtonElement>(null);
    const buttonRef = (ref || internalRef) as React.RefObject<HTMLButtonElement>;

    React.useImperativeHandle(ref, () => buttonRef.current!);

    React.useEffect(() => {
      if (triggerRef.current === null && buttonRef.current) {
        (triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = buttonRef.current;
      }
    }, [triggerRef, buttonRef]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setOpen(!open);
      onClick?.(e);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ref: buttonRef,
        onClick: handleClick,
        ...props,
      } as any);
    }

    return (
      <button ref={buttonRef} onClick={handleClick} {...props}>
        {children}
      </button>
    );
  }
);
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  children: React.ReactNode;
}

const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ align = 'start', sideOffset = 4, className, children, ...props }, ref) => {
    const context = React.useContext(DropdownMenuContext);
    if (!context) throw new Error('DropdownMenuContent must be used within DropdownMenu');

    const { open, setOpen, triggerRef, contentRef } = context;
    const internalRef = React.useRef<HTMLDivElement>(null);
    const divRef = (ref || internalRef) as React.RefObject<HTMLDivElement>;
    const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

    React.useImperativeHandle(ref, () => divRef.current!);

    React.useEffect(() => {
      if (contentRef.current === null && divRef.current) {
        (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = divRef.current;
      }
    }, [contentRef, divRef]);

    // Calculate position immediately when opening
    React.useEffect(() => {
      if (!open) {
        setPosition(null);
        return;
      }

      if (!triggerRef.current) return;

      // Get initial position from trigger immediately
      const triggerRect = triggerRef.current.getBoundingClientRect();
      let top = triggerRect.bottom + sideOffset;
      let left = triggerRect.left;

      // Align horizontally (initial estimate)
      if (align === 'end') {
        left = triggerRect.right;
      } else if (align === 'center') {
        left = triggerRect.left + triggerRect.width / 2;
      }

      setPosition({ top, left });

      // Fine-tune after content is rendered and measured
      const timeoutId = setTimeout(() => {
        if (!triggerRef.current || !divRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const contentRect = divRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top = triggerRect.bottom + sideOffset;
        let left = triggerRect.left;

        // Align horizontally
        if (align === 'end') {
          left = triggerRect.right - contentRect.width;
        } else if (align === 'center') {
          left = triggerRect.left + (triggerRect.width - contentRect.width) / 2;
        }

        // Adjust if content goes off screen
        if (left + contentRect.width > viewportWidth) {
          left = viewportWidth - contentRect.width - 8;
        }
        if (left < 8) {
          left = 8;
        }

        // If content goes below viewport, show above trigger
        if (top + contentRect.height > viewportHeight) {
          top = triggerRect.top - contentRect.height - sideOffset;
        }

        setPosition({ top, left });
      }, 0);

      return () => clearTimeout(timeoutId);
    }, [open, align, sideOffset, triggerRef]);

    if (!open || !position) return null;

    const content = (
      <div
        ref={divRef}
        className={cn(
          'fixed z-[9999] min-w-[8rem] overflow-hidden rounded-md border border-gray-200 bg-white p-1 text-gray-950 shadow-lg',
          'transition-opacity duration-150 ease-out',
          className
        )}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          opacity: open ? 1 : 0,
        }}
        {...props}
      >
        {children}
      </div>
    );

    return createPortal(content, document.body);
  }
);
DropdownMenuContent.displayName = 'DropdownMenuContent';

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'destructive';
  inset?: boolean;
  children: React.ReactNode;
}

const DropdownMenuItem = React.forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ className, variant = 'default', inset, children, onClick, ...props }, ref) => {
    const context = React.useContext(DropdownMenuContext);
    if (!context) throw new Error('DropdownMenuItem must be used within DropdownMenu');

    const { setOpen } = context;

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      // Stop propagation to prevent outside click handler from firing
      e.stopPropagation();
      // Call the user's onClick handler first
      if (onClick) {
        onClick(e);
      }
      // Close the dropdown after onClick executes
      // Use setTimeout to ensure React processes the onClick state update first
      setTimeout(() => {
        setOpen(false);
      }, 10);
    };

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex cursor-pointer select-none items-center justify-start rounded-sm px-3 py-2 text-sm outline-none transition-colors',
          'hover:bg-gray-100 focus:bg-gray-100 focus:text-gray-900',
          variant === 'destructive' &&
            'text-red-600 hover:bg-red-50 focus:bg-red-50 focus:text-red-700 [&_svg]:text-red-600',
          inset && 'pl-8',
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DropdownMenuItem.displayName = 'DropdownMenuItem';

const DropdownMenuGroup = ({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  return <div {...props}>{children}</div>;
};
DropdownMenuGroup.displayName = 'DropdownMenuGroup';

const DropdownMenuLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { inset?: boolean }>(
  ({ className, inset, ...props }, ref) => {
    return <div ref={ref} className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)} {...props} />;
  }
);
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

const DropdownMenuSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('-mx-1 my-1 h-px bg-gray-200', className)} {...props} />;
  }
);
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />;
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

// Placeholder components for compatibility (not implemented but exported)
const DropdownMenuCheckboxItem = () => null;
const DropdownMenuRadioItem = () => null;
const DropdownMenuRadioGroup = () => null;
const DropdownMenuSub = () => null;
const DropdownMenuSubContent = () => null;
const DropdownMenuSubTrigger = () => null;
const DropdownMenuPortal = ({ children }: { children: React.ReactNode }) => <>{children}</>;

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
